import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { MOVE_TYPES, getRandomMove, getRandomKeywords, DEFAULT_KEYWORD_CATEGORIES } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { Shuffle, Sparkles, Settings, Plus, X, Search, Zap, Info } from 'lucide-react';
import MoveDetailModal from './MoveDetailModal';

const ShuffleView = () => {
  const { user, arsenal } = useAuth();
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [moveMode, setMoveMode] = useState('count');
  const [moveCount, setMoveCount] = useState(1);
  const [specificMoves, setSpecificMoves] = useState('');
  const [selectedMovesList, setSelectedMovesList] = useState([]);
  const [moveSearchTerm, setMoveSearchTerm] = useState('');
  const [variationMode, setVariationMode] = useState('count');
  const [variationCount, setVariationCount] = useState(1);
  const [specificVariations, setSpecificVariations] = useState('');
  const [selectedVariationsList, setSelectedVariationsList] = useState([]);
  const [variationSearchTerm, setVariationSearchTerm] = useState('');
  const [resultMoves, setResultMoves] = useState([]);
  const [resultFlavors, setResultFlavors] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [reverseMove, setReverseMove] = useState(false);
  const [associateCustomToRandom, setAssociateCustomToRandom] = useState({ moves: false, variations: false });
  const [selectedMove, setSelectedMove] = useState(null);

  let userVariations = (user?.customVariations && Object.keys(user.customVariations).length > 0)
    ? user.customVariations
    : DEFAULT_KEYWORD_CATEGORIES;

  // Correction : forcer le fallback si aucune variation n'est trouvée
  if (!Object.values(userVariations).flat().length) {
    userVariations = DEFAULT_KEYWORD_CATEGORIES;
    console.log('Fallback forcé sur DEFAULT_KEYWORD_CATEGORIES', userVariations);
  } else {
    console.log('userVariations initial:', userVariations);
  }
  const userMoves = user?.moves?.filter(m => arsenal.includes(m.id)) || [];

  const handleTypeToggle = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleAddSelectedMove = (moveName) => {
    if (!selectedMovesList.includes(moveName)) {
      const newList = [...selectedMovesList, moveName];
      setSelectedMovesList(newList);
      setSpecificMoves(newList.join(', '));
    }
  };

  const handleRemoveSelectedMove = (moveName) => {
    const newList = selectedMovesList.filter(m => m !== moveName);
    setSelectedMovesList(newList);
    setSpecificMoves(newList.join(', '));
  };

  const handleAddSelectedVariation = (variation) => {
    if (!selectedVariationsList.includes(variation)) {
      const newList = [...selectedVariationsList, variation];
      setSelectedVariationsList(newList);
      setSpecificVariations(newList.join(', '));
    }
  };

  const handleRemoveSelectedVariation = (variation) => {
    const newList = selectedVariationsList.filter(v => v !== variation);
    setSelectedVariationsList(newList);
    setSpecificVariations(newList.join(', '));
  };

  const handleSpecificMovesTextChange = (value) => {
    setSpecificMoves(value);
    const moves = value.split(',').map(m => m.trim()).filter(m => m);
    setSelectedMovesList(moves);
  };

  const handleSpecificVariationsTextChange = (value) => {
    setSpecificVariations(value);
    const variations = value.split(',').map(v => v.trim()).filter(v => v);
    setSelectedVariationsList(variations);
  };

  const generateShuffle = () => {
    setIsShuffling(true);
    
    setTimeout(() => {
      let moves = [];
      let variations = [];

      // Génération des moves
      if (moveMode === 'count') {
        for (let i = 0; i < moveCount; i++) {
          const randomType = selectedTypes.length > 0 
            ? selectedTypes[Math.floor(Math.random() * selectedTypes.length)]
            : null;
          const randomMove = getRandomMove(randomType, userMoves);
          if (randomMove) {
            let moveName = randomMove.name;
            if (reverseMove && Math.random() < 0.5) moveName = `Otherside ${moveName}`;
            moves.push(moveName);
          }
        }
      } else {
        let customMoves = specificMoves.split(',').map(m => m.trim()).filter(m => m);
        if (reverseMove) {
          customMoves = customMoves.map(m => (Math.random() < 0.5 ? `Otherside ${m}` : m));
        }
        if (associateCustomToRandom.moves && moveCount > 0) {
          // Toujours générer moveCount moves, en complétant avec du random si besoin, ou en coupant si trop
          const already = new Set(customMoves);
          let needed = moveCount - customMoves.length;
          let poolBase = userMoves
            .filter(m => selectedTypes.length === 0 || selectedTypes.includes(m.type))
            .map(m => m.name);
          let pool = poolBase.filter(m => !already.has(m));
          while (needed > 0) {
            if (pool.length === 0) {
              // Autoriser les doublons si le pool est épuisé
              pool = [...poolBase];
            }
            const idx = Math.floor(Math.random() * pool.length);
            let moveName = pool[idx];
            if (reverseMove && Math.random() < 0.5) moveName = `Otherside ${moveName}`;
            customMoves.push(moveName);
            needed--;
            pool.splice(idx, 1);
          }
          // Mélanger aléatoirement l'ordre final pour éviter que les moves personnalisés restent en premier
          moves = customMoves.slice(0, moveCount).sort(() => Math.random() - 0.5);
        } else {
          moves = customMoves;
        }
      }

      // Génération des variations (indépendante du mode de moves)
      if (variationMode === 'count') {
        // Génération aléatoire de variations
        const allVariations = Object.values(userVariations).flat();
        const shuffledVariations = allVariations.sort(() => Math.random() - 0.5);
        variations = shuffledVariations.slice(0, variationCount);
      } else {
        let customVars = specificVariations.split(',').map(v => v.trim()).filter(v => v);
        if (associateCustomToRandom.variations && variationCount > 0) {
          // Toujours générer variationCount variations, en complétant avec du random si besoin, ou en coupant si trop
          const already = new Set(customVars);
          let needed = variationCount - customVars.length;
          let poolBase = Object.values(userVariations).flat();
          let pool = poolBase.filter(v => !already.has(v));
          while (needed > 0) {
            if (pool.length === 0) {
              // Autoriser les doublons si le pool est épuisé
              pool = [...poolBase];
            }
            const idx = Math.floor(Math.random() * pool.length);
            const varName = pool[idx];
            customVars.push(varName);
            needed--;
            pool.splice(idx, 1);
          }
          // Mélanger aléatoirement l'ordre final pour éviter que les variations personnalisées restent en premier
          variations = customVars.slice(0, variationCount).sort(() => Math.random() - 0.5);
        } else {
          variations = customVars;
        }
      }

      setResultMoves(avoidConsecutiveDuplicates(moves));
      setResultFlavors(avoidConsecutiveDuplicates(variations));
      setIsShuffling(false);
    }, 800);
  };

  // Fonction utilitaire pour éviter les doublons consécutifs
  function avoidConsecutiveDuplicates(arr) {
    if (arr.length < 2) return arr;
    
    // Vérifier s'il y a des doublons consécutifs
    let hasConsecutiveDuplicates = false;
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] === arr[i-1]) {
        hasConsecutiveDuplicates = true;
        break;
      }
    }
    
    // Si pas de doublons consécutifs, retourner tel quel
    if (!hasConsecutiveDuplicates) return arr;
    
    // Sinon, regénérer complètement l'ordre aléatoirement
    // On fait plusieurs tentatives pour éviter les doublons
    for (let attempt = 0; attempt < 10; attempt++) {
      const shuffled = [...arr].sort(() => Math.random() - 0.5);
      let hasDuplicates = false;
      for (let i = 1; i < shuffled.length; i++) {
        if (shuffled[i] === shuffled[i-1]) {
          hasDuplicates = true;
          break;
        }
      }
      if (!hasDuplicates) {
        return shuffled;
      }
    }
    
    // Si après 10 tentatives on a toujours des doublons, on retourne l'original
    // mais on ajoute un préfixe pour différencier
    return arr.map((item, index) => 
      index > 0 && item === arr[index-1] ? `variation ${item}` : item
    );
  }

  const getTypeColor = (type) => {
    const colors = {
      toprock: 'bg-white text-black border-black hover:bg-gray-100',
      footwork: 'bg-black text-white border-white hover:bg-gray-900',
      powermoves: 'bg-white text-black border-black hover:bg-gray-100',
      freezes: 'bg-black text-white border-white hover:bg-gray-900',
      transitions: 'bg-white text-black border-black hover:bg-gray-100'
    };
    return colors[type] || 'bg-gray-500 text-white border-gray-400';
  };

  const getAvailableMoves = () => {
    const moves = selectedTypes.length > 0 
      ? userMoves.filter(move => selectedTypes.includes(move.type))
      : userMoves;
    
    return moveSearchTerm
      ? moves.filter(move => move.name.toLowerCase().includes(moveSearchTerm.toLowerCase()))
      : moves;
  };

  const getFilteredVariations = () => {
    const filteredCategories = {};
    
    Object.entries(userVariations).forEach(([category, variations]) => {
      const filtered = variationSearchTerm
        ? variations.filter(variation => 
            variation.toLowerCase().includes(variationSearchTerm.toLowerCase()) ||
            category.toLowerCase().includes(variationSearchTerm.toLowerCase())
          )
        : variations;
      
      if (filtered.length > 0) {
        filteredCategories[category] = filtered;
      }
    });
    
    return filteredCategories;
  };

  const findMoveByName = (moveName) => {
    // Enlever le préfixe "reverse" si présent
    const cleanName = moveName.replace(/^Otherside\s+/i, '');
    return userMoves.find(move => move.name.toLowerCase() === cleanName.toLowerCase());
  };

  return (
    <div className="min-h-screen bg-black text-white space-y-6">
      {userMoves.length === 0 && (
        <div className="bg-yellow-100 text-yellow-900 border-l-4 border-yellow-500 p-4 rounded max-w-2xl mx-auto mt-8 text-center font-bold">
          To use the shuffle generator, you must first add moves to your arsenal from the Breaking Encyclopedia.
        </div>
      )}
      {/* Header */}
      <div className="text-center border-b-4 bg-white pb-8">
        <div className="py-8">
          <div className="flex justify-center items-center space-x-4 mb-4">
            <Zap className="w-8 h-8 text-white" />
            <h2 className="text-4xl font-black text-white tracking-wider">
              SHUFFLE GENERATOR
            </h2>
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-300 text-lg font-medium tracking-wide">
            MIX • CREATE • BREAK • REPEAT
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4">
        {/* Configuration Panel */}
        <Card className="border-4 bg-white bg-black">
          <CardHeader className="border-b-2 bg-white">
            <CardTitle className="flex items-center gap-2 text-white font-black text-xl">
              <Settings className="w-6 h-6" />
              SESSION CONFIGURATION
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            {/* Move Types */}
            <div>
              <label className="text-white font-bold text-sm mb-3 block tracking-wide">SELECT YOUR STYLES</label>
              <div className="flex flex-wrap gap-3">
                {MOVE_TYPES.map(type => (
                  <Badge 
                    key={type}
                    className={`cursor-pointer transition-all duration-200 font-bold text-sm px-4 py-2 border-2 ${
                      selectedTypes.includes(type) 
                        ? getTypeColor(type) + ' ring-4 ring-white' 
                        : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'
                    }`}
                    onClick={() => handleTypeToggle(type)}
                  >
                    {type.toUpperCase()}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator className="bg-white h-0.5" />

            {/* Moves Configuration */}
            <div className="space-y-4">
              <label className="text-white font-bold text-sm block tracking-wide">MOVE SETUP</label>
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-2">
                  <Checkbox 
                    checked={reverseMove}
                    onCheckedChange={setReverseMove}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                  />
                  <span className="text-white font-medium">Otherside (add "Otherside" before some moves)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    checked={moveMode === 'count'}
                    onCheckedChange={() => setMoveMode('count')}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                  />
                  <span className="text-white font-medium">Random Flow</span>
                </div>
                {moveMode === 'count' && (
                  <div className="ml-6 flex items-center gap-3">
                    <button
                      type="button"
                      aria-label="Diminuer"
                      onClick={() => setMoveCount(c => Math.max(1, c - 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-black text-white border-2 border-white text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50"
                      disabled={moveCount <= 1}
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-lg font-bold text-white select-none">{moveCount}</span>
                    <button
                      type="button"
                      aria-label="Augmenter"
                      onClick={() => setMoveCount(c => Math.min(5, c + 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-black text-white border-2 border-white text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50"
                      disabled={moveCount >= 5}
                    >
                      +
                    </button>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    checked={moveMode === 'specific'}
                    onCheckedChange={() => setMoveMode('specific')}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                  />
                  <span className="text-white font-medium">Custom Selection</span>
                </div>
                {moveMode === 'specific' && (
                  <div className="ml-6 space-y-3">
                    <div className="flex items-center mb-2">
                      <Checkbox
                        checked={associateCustomToRandom.moves}
                        onCheckedChange={v => setAssociateCustomToRandom(a => ({ ...a, moves: v }))}
                        className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                      />
                      <span className="text-white font-medium ml-2">Associate with random</span>
                    </div>
                    <Textarea
                      placeholder="Type move names, separated by commas..."
                      value={specificMoves}
                      onChange={(e) => handleSpecificMovesTextChange(e.target.value)}
                      className="resize-none bg-white text-black border-2 border-black focus:ring-2 focus:ring-gray-400 placeholder-gray-500"
                      rows={3}
                    />
                    
                    {/* Move Search & Selection */}
                    <div className="space-y-3">
                      <Label className="text-xs text-gray-300 font-medium">Or pick from your arsenal:</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-black" />
                        <Input
                          placeholder="Search moves..."
                          value={moveSearchTerm}
                          onChange={(e) => setMoveSearchTerm(e.target.value)}
                          className="pl-10 bg-white text-black border-2 border-black focus:ring-2 focus:ring-gray-400 placeholder-gray-500"
                        />
                      </div>
                      <ScrollArea className="h-32 border-2 border-white rounded bg-gray-900 p-2">
                        <div className="space-y-1">
                          {getAvailableMoves().map(move => (
                            <div key={move.id} className="flex items-center justify-between p-2 hover:bg-gray-800 rounded transition-colors group">
                              <div className="flex-1">
                                <span className="text-sm text-white font-medium">{move.name}</span>
                                <Badge className={`ml-2 text-xs border ${getTypeColor(move.type)} scale-75`}>
                                  {move.type}
                                </Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-white hover:text-black hover:bg-white transition-all"
                                onClick={() => handleAddSelectedMove(move.name)}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          {getAvailableMoves().length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-4">No moves found</p>
                          )}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Selected Moves Display */}
                    {selectedMovesList.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-300 font-medium">Selected moves:</Label>
                        <div className="flex flex-wrap gap-2">
                          {selectedMovesList.map(move => (
                            <Badge key={move} className="bg-white text-black font-medium px-3 py-1 border-2 border-black hover:bg-gray-100 transition-all">
                              {move}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-4 w-4 p-0 ml-2 text-black hover:text-red-600"
                                onClick={() => handleRemoveSelectedMove(move)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Separator className="bg-white h-0.5" />

            {/* Variations Configuration */}
            <div className="space-y-4">
              <label className="text-white font-bold text-sm block tracking-wide">FLAVOR SETUP</label>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    checked={variationMode === 'count'}
                    onCheckedChange={() => setVariationMode('count')}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                  />
                  <span className="text-white font-medium">Random Spice</span>
                </div>
                {variationMode === 'count' && (
                  <div className="ml-6 flex items-center gap-3">
                    <button
                      type="button"
                      aria-label="Diminuer"
                      onClick={() => setVariationCount(c => Math.max(0, c - 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-black text-white border-2 border-white text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50"
                      disabled={variationCount <= 0}
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-lg font-bold text-white select-none">{variationCount}</span>
                    <button
                      type="button"
                      aria-label="Augmenter"
                      onClick={() => setVariationCount(c => Math.min(5, c + 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-black text-white border-2 border-white text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50"
                      disabled={variationCount >= 5}
                    >
                      +
                    </button>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    checked={variationMode === 'specific'}
                    onCheckedChange={() => setVariationMode('specific')}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                  />
                  <span className="text-white font-medium">Custom Flavor</span>
                </div>
                {variationMode === 'specific' && (
                  <div className="ml-6 space-y-3">
                    <div className="flex items-center mb-2">
                      <Checkbox
                        checked={associateCustomToRandom.variations}
                        onCheckedChange={v => setAssociateCustomToRandom(a => ({ ...a, variations: v }))}
                        className="border-white data-[state=checked]:bg-white data-[state=checked]:border-white"
                      />
                      <span className="text-white font-medium ml-2">Associate with random</span>
                    </div>
                    <Textarea
                      placeholder="Type variations, separated by commas..."
                      value={specificVariations}
                      onChange={(e) => handleSpecificVariationsTextChange(e.target.value)}
                      className="resize-none bg-white text-black border-2 border-black focus:ring-2 focus:ring-gray-400 placeholder-gray-500"
                      rows={3}
                    />
                    
                    {/* Variation Search & Selection */}
                    <div className="space-y-3">
                      <Label className="text-xs text-gray-300 font-medium">Or pick from your flavor bank:</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-black" />
                        <Input
                          placeholder="Search variations..."
                          value={variationSearchTerm}
                          onChange={(e) => setVariationSearchTerm(e.target.value)}
                          className="pl-10 bg-white text-black border-2 border-black focus:ring-2 focus:ring-gray-400 placeholder-gray-500"
                        />
                      </div>
                      <ScrollArea className="h-32 border-2 border-white rounded bg-gray-900 p-2">
                        <div className="space-y-2">
                          {Object.entries(getFilteredVariations()).map(([category, variations]) => (
                            <div key={category}>
                              <h4 className="text-xs font-bold text-gray-300 capitalize mb-1 tracking-wide">
                                {category.replace(/([A-Z])/g, ' $1').trim()}
                              </h4>
                              <div className="space-y-1 ml-2">
                                {variations.map(variation => (
                                  <div key={variation} className="flex items-center justify-between p-1 hover:bg-gray-800 rounded transition-colors group">
                                    <span className="text-xs text-white font-medium">{variation}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-white hover:text-black hover:bg-white transition-all"
                                      onClick={() => handleAddSelectedVariation(variation)}
                                    >
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          {Object.keys(getFilteredVariations()).length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-4">No variations found</p>
                          )}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Selected Variations Display */}
                    {selectedVariationsList.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-300 font-medium">Selected flavors:</Label>
                        <div className="flex flex-wrap gap-2">
                          {selectedVariationsList.map(variation => (
                            <Badge key={variation} className="bg-black text-white font-medium px-3 py-1 border-2 border-white hover:bg-gray-800 transition-all">
                              {variation}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-4 w-4 p-0 ml-2 text-white hover:text-red-400"
                                onClick={() => handleRemoveSelectedVariation(variation)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Button 
              onClick={generateShuffle}
              disabled={isShuffling}
              className="w-full bg-white text-black font-black text-lg py-4 hover:bg-gray-200 transition-all duration-200 border-2 border-black"
              size="lg"
            >
              {isShuffling ? (
                <>
                  <Sparkles className="w-6 h-6 mr-2" />
                  MIXING THE BEAT...
                </>
              ) : (
                <>
                  <Shuffle className="w-6 h-6 mr-2" />
                  DROP THE COMBO
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result Panel */}
        <Card className="border-4 bg-white bg-black">
          <CardHeader className="border-b-2 bg-white">
            <CardTitle className="flex items-center gap-2 text-white font-black text-xl">
              <Sparkles className="w-6 h-6" />
              YOUR COMBO
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="min-h-[300px]">
              {(resultMoves.length > 0 || resultFlavors.length > 0) ? (
                <div className="space-y-6">
                  {/* Moves Section */}
                  {resultMoves.length > 0 && (
                    <div className="border-2 border-white p-4">
                      <h3 className="text-lg font-black text-white mb-3 tracking-wide">MOVES</h3>
                      <div className="space-y-2">
                        {resultMoves.map((move, index) => {
                          const moveData = findMoveByName(move);
                          return (
                            <div 
                              key={index}
                              className="text-xl font-bold text-white bg-gray-900 border border-white p-3 tracking-wider flex items-center justify-between group"
                              style={{ animationDelay: `${index * 200}ms` }}
                            >
                              <span>{move}</span>
                              {moveData && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-white hover:text-black hover:bg-white transition-all opacity-0 group-hover:opacity-100"
                                  onClick={() => setSelectedMove(moveData)}
                                  title="View move details"
                                >
                                  <Info className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Flavors Section */}
                  {resultFlavors.length > 0 && (
                    <div className="border-2 border-white p-4">
                      <h3 className="text-lg font-black text-white mb-3 tracking-wide">FLAVORS</h3>
                      <div className="space-y-2">
                        {resultFlavors.map((flavor, index) => (
                          <div 
                            key={index}
                            className="text-xl font-bold text-black bg-white border border-black p-3 tracking-wider"
                            style={{ animationDelay: `${(resultMoves.length + index) * 200}ms` }}
                          >
                            {flavor}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <Badge className="bg-white text-black font-bold px-4 py-2 text-sm tracking-wide border-2 border-black">
                      🔥 FRESH COMBO INCOMING 🔥
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-center text-gray-400 space-y-4">
                  <div>
                    <div className="text-8xl mb-6">🎤</div>
                    <p className="text-xl font-bold text-white tracking-wide">READY TO DROP?</p>
                    <p className="text-gray-300">Configure your setup and hit that button!</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Move Detail Modal */}
      <MoveDetailModal 
        move={selectedMove}
        isOpen={!!selectedMove}
        onClose={() => setSelectedMove(null)}
      />
    </div>
  );
};

export default ShuffleView;